<script setup lang="ts">
import getTTS from '@/composables/tts.js';
import { ref, computed, onUnmounted } from 'vue';
import {
  getDocument,
  GlobalWorkerOptions,
} from 'pdfjs-dist/legacy/build/pdf.mjs';
import workerSrc from 'pdfjs-dist/legacy/build/pdf.worker.mjs?url';
import { convertEmojisToText } from '@/utils/convertEmojisToText';

GlobalWorkerOptions.workerSrc = workerSrc;

interface Props {
  pdf?: string;
  buttonPosition?: 'justify-start' | 'justify-center' | 'justify-end';
  contentClassName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  buttonPosition: 'justify-end',
  contentClassName: 'tts-content',
});

const audioEnabled = ref(false);
const audioPlaying = ref(false);
const ttsText = ref<string[]>([]);
const audioSources = ref<HTMLAudioElement[]>([]);
const currentAudioIndex = ref(0);
const isFetchingTTS = ref(false);
const shouldCancelFetch = ref(false);

onUnmounted(() => {
  stopAllAudio();
});

const stopAllAudio = () => {
  shouldCancelFetch.value = true;
  audioSources.value.forEach((audio) => {
    audio.pause();
    audio.currentTime = 0;
  });
  audioSources.value = [];
  currentAudioIndex.value = 0;
  audioPlaying.value = false;
};

// Split text into chunks of max 1000 characters, keeping words intact
const splitTextIntoChunks = (
  text: string,
  maxLength: number = 1000
): string[] => {
  const chunks: string[] = [];
  let currentChunk = '';

  const words = text.split(/\s+/);

  for (const word of words) {
    // If adding this word exceeds the limit and we have content, save the chunk
    if (
      currentChunk.length + word.length + 1 > maxLength &&
      currentChunk.length > 0
    ) {
      chunks.push(currentChunk.trim());
      currentChunk = '';
    }

    // If a single word is longer than maxLength, split it
    if (word.length > maxLength) {
      if (currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }
      // Split long word into chunks
      for (let i = 0; i < word.length; i += maxLength) {
        chunks.push(word.substring(i, i + maxLength));
      }
    } else {
      currentChunk += (currentChunk.length > 0 ? ' ' : '') + word;
    }
  }

  // Add the last chunk if it has content
  if (currentChunk.trim().length > 0) {
    chunks.push(currentChunk.trim());
  }

  return chunks;
};

const backgroundColor = computed(() => {
  return audioPlaying.value ? 'bg-orange' : 'bg-white';
});

const toggleAudio = async () => {
  if (audioEnabled.value) {
    stopAllAudio();
    audioEnabled.value = false;
    return;
  }

  // Get the text content
  let textContent: string;
  if (!props.pdf) {
    textContent = getTextByAriaLabel();
  } else {
    textContent = await getTextByPdf();
  }

  // Convert emojis to text for TTS
  textContent = convertEmojisToText(textContent) || '';

  ttsText.value = splitTextIntoChunks(textContent);

  // Reset state
  audioSources.value = [];
  currentAudioIndex.value = 0;
  shouldCancelFetch.value = false;
  audioEnabled.value = true;
  audioPlaying.value = true;
  isFetchingTTS.value = true;

  // Generate TTS for each chunk and play as soon as the first one is ready
  for (let i = 0; i < ttsText.value.length; i++) {
    // Check if user has stopped playback
    if (shouldCancelFetch.value) {
      isFetchingTTS.value = false;
      audioPlaying.value = false;
      return;
    }

    const chunk = ttsText.value[i];
    const { error, data } = await getTTS(chunk);

    if (error.value) {
      console.error('TTS error:', error.value);
      continue;
    }

    if (data.value?.source) {
      const audio = new Audio(data.value.source);
      audioSources.value.push(audio);

      // Start playing the first audio chunk immediately
      if (i === 0) {
        playNextAudio();
      }
    }
  }

  isFetchingTTS.value = false;

  if (audioSources.value.length === 0) {
    audioEnabled.value = false;
    audioPlaying.value = false;
  }
};

const playNextAudio = () => {
  if (!audioEnabled.value || shouldCancelFetch.value) {
    return;
  }

  if (currentAudioIndex.value >= audioSources.value.length) {
    // Check if we're still fetching more chunks
    if (isFetchingTTS.value) {
      setTimeout(playNextAudio, 100);
      return;
    }

    // All audio chunks have been played
    audioEnabled.value = false;
    audioPlaying.value = false;
    currentAudioIndex.value = 0;
    return;
  }

  const audio = audioSources.value[currentAudioIndex.value];
  audio.addEventListener('ended', () => {
    currentAudioIndex.value++;
    playNextAudio();
  });

  audio.play();
};

function getTextByAriaLabel(): string {
  const parentElements = document.getElementsByClassName(
    props.contentClassName
  );

  if (parentElements.length === 0) {
    return '';
  }

  const parentElement = parentElements[0];
  const nodeList = parentElement.querySelectorAll('[aria-label]');

  const strings = [];
  for (let i = 0; i < nodeList.length; i++) {
    strings.push(nodeList[i].ariaLabel);
  }

  return strings.join('. ');
}

const getTextByPdf = async (): Promise<string> => {
  const pdf = await getDocument(props.pdf).promise;

  let text = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    text +=
      content.items.map((item) => ('str' in item ? item.str : '')).join(' ') +
      '\n';
  }

  const cleanedText = text.replace(/[\x00-\x1F\x7F]/g, '');
  return cleanedText;
};
</script>

<template>
  <div class="flex" :class="buttonPosition">
    <div
      class="rounded-full w-19 h-19 flex justify-center items-center relative duration-300 transition border-2 border-neutral-300 shadow-lg"
      :class="backgroundColor"
      @click="toggleAudio"
    >
      <fa-icon
        v-if="!audioPlaying"
        :icon="['far', 'volume']"
        class="text-4xl"
      />
      <div v-else class="sound-wave flex justify-around items-center">
        <i class="bar" />
        <i class="bar" />
        <i class="bar" />
        <i class="bar" />
        <i class="bar" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped="true">
.sound-wave {
  width: 50px;
  height: 65px;

  .bar {
    width: 6px;
    margin-right: 1px;
    height: 65px;
    background: #ffffff;
    animation: sound 0ms -800ms linear infinite alternate;
    transition: height 0.4s;
    border-radius: 10px;
  }
}

@keyframes sound {
  0% {
    opacity: 0.35;
    height: 3px;
  }
  100% {
    opacity: 1;
    height: 40px;
  }
}

.bar:nth-child(1) {
  animation-duration: 374ms;
}
.bar:nth-child(2) {
  animation-duration: 333ms;
}
.bar:nth-child(3) {
  animation-duration: 307ms;
}
.bar:nth-child(4) {
  animation-duration: 358ms;
}
.bar:nth-child(5) {
  animation-duration: 300ms;
}
</style>
